import React, { useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import ORRIOwnershipCard from './ORRIOwnershipCard.redesign';
import Card from 'src/components/common/Card.redesign';
import AnimateHeight from 'react-animate-height';
import AddressUtil from 'src/util/Address';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';

interface IWIOwnershipCardProps extends Partial<Pick<UseFormMethods, 'register' | 'errors'>> {
    ownership: WIOwnership | WIContractOwnership;
    owner: Owner;
    index: number;
    owners: Array<Owner>;
    namePrefix?: string;

    addORRI: (parentOwnershipId: number) => void;
    copyORRI: (ownershipId: number) => void;
    deleteORRI: (ownershipId: number) => void;
    deleteLeaseholdOwnership: (leaseholdOwnershipId: number) => void;
    showDialog: (dialogName: string) => void;
    openOwnershipNotes: (ownershipId: number) => void;
    onWIInterestChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const WIOwnershipCard = (props: IWIOwnershipCardProps) => {
    const [additionalHeight, setAdditionalHeight] = useState<string | number>(0);
    const [namePrefix, setNamePrefix] = useState('leasehold.leaseholdOwnerships');

    const actions: ContextMenuAction[] = [
        { text: 'Add ORRI', action: () => props.addORRI(props.ownership.id) },
        { text: 'Copy ORRI', action: () => props.copyORRI(props.ownership.id) },
    ];
    if (props.ownership?.orriOwnerships?.length > 0) {
        actions.push({ text: 'Delete ORRI', action: () => props.deleteORRI(props.ownership.id) });
    }
    actions.push(
        ...[
            { text: 'Detach WI', action: () => props.deleteLeaseholdOwnership(props.ownership.id) },
            { text: 'Ownership Notes', action: () => props.openOwnershipNotes(props.ownership.id) },
        ]
    );

    useEffect(() => {
        if (!!props.namePrefix) {
            setNamePrefix(props.namePrefix);
        }
    }, [props.namePrefix]);

    const orriOwnership = () => {
        let ownerships = props.ownership.orriOwnerships as unknown as WIOwnership[];

        return ownerships.map((ao, index) => {
            const orriOwner = props.owners.find((o) => o.ownerLookUpId === ao.ownerLookUpId);

            const orriActions: ContextMenuAction[] = [
                { text: 'Delete Ownership', action: () => props.deleteLeaseholdOwnership(ao.id) },
                { text: 'Ownership Notes', action: () => props.openOwnershipNotes(ao.id) },
            ];
            return (
                <ORRIOwnershipCard
                    key={index}
                    ownership={ao}
                    owner={orriOwner}
                    parentIndex={props.index}
                    index={index}
                    actions={orriActions}
                    register={props.register}
                    errors={props.errors}
                    onWIInterestChange={props.onWIInterestChange}
                    prefix={`${namePrefix}[${props.index}].orriOwnerships`}
                />
            );
        });
    };

    const toggleAdditionalOwnerships = () => {
        const newHeight = additionalHeight === 'auto' ? 0 : 'auto';
        setAdditionalHeight(newHeight);
    };

    return (
        <Card badge={`${props.ownership.id}`} contextActions={actions} newContext={true} showMenu={true} style={{ margin: 0 }}>
            {props?.owner && (
                <>
                    <div className="wi-ownership-card">
                        <div>
                            <div>
                                <span className="name">
                                    {props?.owner?.name || 'N/A'} - {props?.owner?.ownerType || 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span>{AddressUtil.concatWithCommas(props.owner.addressLine1, props.owner.city, props.owner.state)}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <input type="hidden" name={`${namePrefix}[${props.index}].id`} ref={props.register()} />
                            <RHFInputBox
                                label=""
                                name={`${namePrefix}[${props.index}].projectId`}
                                type="hidden"
                                register={props.register}
                                errors={props.errors}
                            />
                            <RHFInputBox
                                label=""
                                name={`${namePrefix}[${props.index}].ownerLookUpId`}
                                type="hidden"
                                register={props.register}
                                errors={props.errors}
                            />
                            <RHFInputBox
                                label=""
                                name={`${namePrefix}[${props.index}].parentOwnershipId`}
                                type="hidden"
                                register={props.register}
                                errors={props.errors}
                            />
                            <RHFInputBox
                                label=""
                                name={`${namePrefix}[${props.index}].ownershipId`}
                                type="hidden"
                                register={props.register}
                                errors={props.errors}
                            />
                            <RHFInputBox
                                label=""
                                name={`${namePrefix}[${props.index}].leaseholdId`}
                                type="hidden"
                                register={props.register}
                                errors={props.errors}
                            />

                            <div className="mr-2">
                                <RHFInputBox
                                    onChange={props.onWIInterestChange}
                                    type="text"
                                    label="WI"
                                    name={`${namePrefix}[${props.index}].decimalInterest`}
                                    register={props.register}
                                    errors={props.errors}
                                />
                            </div>
                            <div className="mr-2">
                                <RHFInputBox
                                    type="text"
                                    disabled={true}
                                    label="ORRI Burden Total"
                                    name={`${namePrefix}[${props.index}].orriInterest`}
                                    register={props.register}
                                    errors={props.errors}
                                />
                            </div>
                            <div>
                                <RHFInputBox
                                    type="text"
                                    disabled={true}
                                    label="NRI"
                                    name={`${namePrefix}[${props.index}].nriInterest`}
                                    register={props.register}
                                    errors={props.errors}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {props.ownership?.orriOwnerships?.length > 0 && (
                <>
                    <div onClick={toggleAdditionalOwnerships} style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{additionalHeight === 'auto' ? '-' : '+'}</span>
                        {` Overrides (${props.ownership.orriOwnerships.length})`}
                    </div>
                    <AnimateHeight duration={300} height={additionalHeight}>
                        {<div style={{ overflowY: 'auto' }}>{orriOwnership()}</div>}
                    </AnimateHeight>
                </>
            )}
        </Card>
    );
};

export default WIOwnershipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/WIOwnershipCard.redesign.tsx